import { useState, useEffect, useRef } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Mail, Monitor, Smartphone, Send, Eye, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  Client: 'bg-blue-100 text-blue-700',
  Restaurant: 'bg-amber-100 text-amber-700',
  Onboarding: 'bg-purple-100 text-purple-700',
};

export default function EmailPreview() {
  useAuth();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [viewMode, setViewMode] = useState('desktop');
  const [sending, setSending] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/email-preview/templates`);
        setTemplates(data.templates || []);
      } catch {
        toast.error('Erreur lors du chargement des templates');
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (!selected) {
      setPreviewHtml('');
      return;
    }
    const loadPreview = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/email-preview/${selected}?base_url=${encodeURIComponent(BASE_URL)}`);
        setPreviewHtml(data);
      } catch {
        toast.error('Erreur lors du chargement de l\'apercu');
      } finally {
        setLoading(false);
      }
    };
    loadPreview();
  }, [selected]);

  useEffect(() => {
    if (!iframeRef.current || !previewHtml) return;
    const doc = iframeRef.current.contentDocument;
    doc.open();
    doc.write(previewHtml);
    doc.close();
  }, [previewHtml, viewMode]);

  const handleSendTest = async () => {
    if (!selected) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/email-preview/${selected}/send-test?base_url=${encodeURIComponent(BASE_URL)}`,
        {}
      );
      toast.success('Email de test envoye !');
    } catch {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const grouped = templates.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  const selectedTemplate = templates.find((t) => t.id === selected);

  return (
    <AdminLayout title="Apercu Emails">
      <div className="space-y-6">
        {/* Controls bar */}
        <div
          className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          data-testid="email-preview-controls"
        >
          {/* Template selector */}
          <div className="flex-1 min-w-0 w-full sm:w-auto">
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="w-full" data-testid="email-template-select">
                <SelectValue placeholder="Choisir un template..." />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(grouped).map(([cat, items]) => (
                  <div key={cat}>
                    <div className="px-2 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {cat}
                    </div>
                    {items.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* View mode toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1">
            <button
              onClick={() => setViewMode('desktop')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'desktop'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="view-mode-desktop"
            >
              <Monitor className="w-4 h-4" />
              Desktop
            </button>
            <button
              onClick={() => setViewMode('mobile')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'mobile'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="view-mode-mobile"
            >
              <Smartphone className="w-4 h-4" />
              Mobile
            </button>
          </div>

          {/* Send test button */}
          <button
            onClick={handleSendTest}
            disabled={!selected || sending}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            data-testid="send-test-email-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Envoyer un test
          </button>
        </div>

        {/* Category badge + info */}
        {selectedTemplate && (
          <div className="flex items-center gap-3" data-testid="selected-template-info">
            <span
              className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                CATEGORY_COLORS[selectedTemplate.category] || 'bg-gray-100 text-gray-600'
              }`}
            >
              {selectedTemplate.category}
            </span>
            <span className="text-sm text-gray-500">
              Donnees fictives - le rendu correspond au format reel
            </span>
          </div>
        )}

        {/* Preview area */}
        <div
          className="bg-gray-200 rounded-xl p-4 sm:p-8 flex justify-center min-h-[600px]"
          data-testid="email-preview-area"
        >
          {!selected ? (
            <div className="flex flex-col items-center justify-center text-gray-400 gap-3">
              <Mail className="w-16 h-16 opacity-40" />
              <p className="text-lg font-medium">Selectionnez un template</p>
              <p className="text-sm">pour afficher l'apercu email</p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : (
            <div
              className="transition-all duration-300 ease-in-out bg-white rounded-lg shadow-lg overflow-hidden"
              style={{
                width: viewMode === 'mobile' ? '375px' : '100%',
                maxWidth: viewMode === 'desktop' ? '700px' : '375px',
              }}
            >
              {/* Simulated browser bar */}
              <div className="bg-gray-100 border-b border-gray-200 px-4 py-2 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 bg-white rounded px-3 py-1 text-xs text-gray-400 truncate flex items-center gap-1.5">
                  <Eye className="w-3 h-3" />
                  Apercu : {selectedTemplate?.label}
                </div>
              </div>
              <iframe
                ref={iframeRef}
                title="Email Preview"
                className="w-full border-0"
                style={{ minHeight: '600px', height: '100%' }}
                sandbox="allow-same-origin"
                data-testid="email-preview-iframe"
              />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
